import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { socketService } from '../services/socketService';
import { apiService } from '../services/api';

// Tipos de datos para la autenticación
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'builder' | 'owner' | 'tenant' | 'guest';
  apartment?: string;
  documentType?: 'cedula' | 'passport' | 'other';
  documentNumber?: string;
  phone?: string;
  managedComplexId?: string;
  managedComplexName?: string;
  assignedComplexes?: Array<{ id: string; name: string }>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Función utilitaria para verificar permisos de votación
export const canUserVote = (user: User | null): boolean => {
  if (!user) return false;
  return ['admin', 'manager', 'owner', 'tenant'].includes(user.role);
};

// Función utilitaria para verificar permisos de administración
export const canUserAdminister = (user: User | null): boolean => {
  if (!user) return false;
  return user.role === 'admin';
};

// Función utilitaria para obtener el nombre del rol para mostrar
export const getRoleDisplayName = (role: string): string => {
  const roleNames = {
    'admin': 'Administrador',
    'manager': 'Manager',
    'builder': 'Constructora',
    'owner': 'Propietario',
    'tenant': 'Arrendatario', 
    'guest': 'Invitado'
  };
  return roleNames[role as keyof typeof roleNames] || 'Usuario';
};

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role?: 'owner' | 'guest';
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshUser: () => Promise<void>;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'REGISTER_START' }
  | { type: 'REGISTER_SUCCESS'; payload: User }
  | { type: 'REGISTER_FAILURE'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'UPDATE_USER'; payload: User };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
    case 'REGISTER_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
    case 'REGISTER_FAILURE':
      return {
        ...state,
        // NO cambiar user ni isAuthenticated si ya estaba desautenticado
        // Solo actualizar error y loading
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
}

// Servicio de autenticación
class AuthService {
  private static instance: AuthService;
  private baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Almacenar tokens en localStorage
  private setTokens(tokens: AuthTokens): void {
    console.log('💾 [AuthService] Almacenando tokens...');
    console.log('💾 [AuthService] Access token para almacenar:', tokens.accessToken);
    console.log('💾 [AuthService] Refresh token para almacenar:', tokens.refreshToken);
    
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    
    // Verificar que se almacenó correctamente
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    console.log('✅ [AuthService] Token almacenado verificado:', storedAccessToken === tokens.accessToken);
    console.log('✅ [AuthService] Refresh token almacenado verificado:', storedRefreshToken === tokens.refreshToken);
  }

  // Obtener token de acceso
  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  // Obtener refresh token
  private getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  // Limpiar tokens
  private clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  // Configurar headers con autenticación
  private getAuthHeaders(): HeadersInit {
    const token = this.getAccessToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // Login
  async login(credentials: LoginCredentials): Promise<{ user: User; tokens: AuthTokens }> {
    const response = await fetch(`${this.baseURL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Error en el login');
    }

    const data = await response.json();
    this.setTokens(data.tokens);
    return data;
  }

  // Registro
  async register(userData: RegisterData): Promise<{ user: User; tokens: AuthTokens }> {
    const response = await fetch(`${this.baseURL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Error en el registro');
    }

    const data = await response.json();
    console.log('🔍 [AuthService] Respuesta del servidor:', data);
    console.log('🔍 [AuthService] Tokens recibidos:', data.tokens);
    console.log('🔍 [AuthService] Access token:', data.tokens?.accessToken);
    console.log('🔍 [AuthService] Refresh token:', data.tokens?.refreshToken);
    
    this.setTokens(data.tokens);
    return data;
  }

  // Obtener perfil del usuario
  async getProfile(): Promise<User> {
    const response = await fetch(`${this.baseURL}/auth/profile`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearTokens();
        throw new Error('Sesión expirada');
      }
      throw new Error('Error al obtener el perfil');
    }

    const data = await response.json();
    return data.user;
  }

  // Logout
  async logout(): Promise<void> {
    try {
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        await fetch(`${this.baseURL}/auth/logout`, {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({ refreshToken }),
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      this.clearTokens();
    }
  }

  // Refrescar token
  async refreshAccessToken(): Promise<AuthTokens> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${this.baseURL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      this.clearTokens();
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    this.setTokens(data.tokens);
    return data.tokens;
  }

  // Verificar si hay token válido
  hasValidToken(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    try {
      // Decodificar JWT para verificar expiración
      const payload = JSON.parse(atob(token.split('.')[1]));
      const isExpired = payload.exp * 1000 < Date.now();
      return !isExpired;
    } catch {
      return false;
    }
  }
}

// Crear contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider del contexto
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const authService = AuthService.getInstance();

  // Inicializar autenticación al cargar la app
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (authService.hasValidToken()) {
          const user = await authService.getProfile();
          dispatch({ type: 'LOGIN_SUCCESS', payload: user });
          
          // Conectar socket si el usuario ya estaba autenticado
          console.log('🔌 [AuthContext] Usuario ya autenticado, conectando socket...');
          await socketService.connect();
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        await authService.logout();
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initAuth();
  }, []);

  // Funciones del contexto
  const login = async (credentials: LoginCredentials): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const { user } = await authService.login(credentials);
      dispatch({ type: 'LOGIN_SUCCESS', payload: user });
      
      // Conectar socket después del login exitoso
      console.log('🔌 [AuthContext] Usuario autenticado, conectando socket...');
      await socketService.connect();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  const register = async (data: RegisterData): Promise<void> => {
    dispatch({ type: 'REGISTER_START' });
    try {
      const { user } = await authService.register(data);
      dispatch({ type: 'REGISTER_SUCCESS', payload: user });
      
      // Conectar socket después del registro exitoso
      console.log('🔌 [AuthContext] Usuario registrado, conectando socket...');
      await socketService.connect();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      dispatch({ type: 'REGISTER_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    const currentAssemblyId = socketService.getCurrentAssembly();

    if (currentAssemblyId) {
      try {
        console.log(`🚪 [AuthContext] Saliendo de asamblea ${currentAssemblyId} antes del logout...`);
        await apiService.leaveAssembly(currentAssemblyId);
        await socketService.leaveAssembly(currentAssemblyId);
      } catch (error) {
        console.warn('⚠️ [AuthContext] No se pudo salir de asamblea antes del logout:', error);
      }
    }

    // Desconectar socket antes del logout
    console.log('🔌 [AuthContext] Desconectando socket antes del logout...');
    socketService.disconnect();
    
    await authService.logout();
    dispatch({ type: 'LOGOUT' });
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const user = await authService.getProfile();
      dispatch({ type: 'UPDATE_USER', payload: user });
    } catch (error) {
      console.error('Error refreshing user:', error);
      await logout();
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    clearError,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook para usar el contexto
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;